import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpDown, ChevronsDownUp, ChevronsUpDown, Layers } from 'lucide-react';
import {
  GROUP_TIMELINE_LAYOUT_LABELS,
  GROUP_TIMELINE_LAYOUT_MODES,
  GROUP_TIMELINE_SORT_LABELS,
  GROUP_TIMELINE_SORT_MODES,
  type GroupTimelineLayoutMode,
  type GroupTimelineSortMode,
} from '../../utils/groupTimeline';
import './GroupTimelineColorControls.css';

interface GroupTimelineViewControlsProps {
  layout: GroupTimelineLayoutMode;
  onLayoutChange: (layout: GroupTimelineLayoutMode) => void;
  sortMode: GroupTimelineSortMode;
  onSortChange: (sort: GroupTimelineSortMode) => void;
  onCollapseAll?: () => void;
  onExpandAll?: () => void;
  pinnedCount?: number;
  pinLimit?: number;
  onClearPins?: () => void;
}

const GroupTimelineViewControls: React.FC<GroupTimelineViewControlsProps> = ({
  layout,
  onLayoutChange,
  sortMode,
  onSortChange,
  onCollapseAll,
  onExpandAll,
  pinnedCount = 0,
  pinLimit = 6,
  onClearPins,
}) => {
  const [open, setOpen] = useState<'layout' | 'sort' | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(null);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="pm-hub-color-controls" ref={rootRef}>
      <div className="pm-hub-color-controls" style={{ position: 'relative' }}>
        <button
          type="button"
          className={`pm-hub-color-trigger${open === 'layout' ? ' is-open' : ''}`}
          aria-haspopup="listbox"
          aria-expanded={open === 'layout'}
          aria-label={`Group timeline by ${GROUP_TIMELINE_LAYOUT_LABELS[layout]}`}
          title="Group sections by project, task group, or supplier"
          onClick={() => setOpen((v) => (v === 'layout' ? null : 'layout'))}
        >
          <Layers size={13} strokeWidth={2} aria-hidden />
          <span>Group: {GROUP_TIMELINE_LAYOUT_LABELS[layout]}</span>
        </button>
        {open === 'layout' && (
          <div className="pm-dropdown pm-hub-color-menu" role="listbox" aria-label="Group timeline by">
            {GROUP_TIMELINE_LAYOUT_MODES.map((option) => (
              <button
                key={option}
                type="button"
                role="option"
                aria-selected={layout === option}
                className={`pm-dropdown-item${layout === option ? ' is-active' : ''}`}
                onClick={() => {
                  onLayoutChange(option);
                  setOpen(null);
                }}
              >
                {GROUP_TIMELINE_LAYOUT_LABELS[option]}
              </button>
            ))}
          </div>
        )}
      </div>
      {layout !== 'supplier' && (
        <div className="pm-hub-color-controls" style={{ position: 'relative' }}>
          <button
            type="button"
            className={`pm-hub-color-trigger${open === 'sort' ? ' is-open' : ''}`}
            aria-haspopup="listbox"
            aria-expanded={open === 'sort'}
            aria-label={`Sort projects by ${GROUP_TIMELINE_SORT_LABELS[sortMode]}`}
            title="Sort project sections by supplier or earliest start"
            onClick={() => setOpen((v) => (v === 'sort' ? null : 'sort'))}
          >
            <ArrowUpDown size={13} strokeWidth={2} aria-hidden />
            <span>Sort: {GROUP_TIMELINE_SORT_LABELS[sortMode]}</span>
          </button>
          {open === 'sort' && (
            <div className="pm-dropdown pm-hub-color-menu" role="listbox" aria-label="Sort projects by">
              {GROUP_TIMELINE_SORT_MODES.map((option) => (
                <button
                  key={option}
                  type="button"
                  role="option"
                  aria-selected={sortMode === option}
                  className={`pm-dropdown-item${sortMode === option ? ' is-active' : ''}`}
                  onClick={() => {
                    onSortChange(option);
                    setOpen(null);
                  }}
                >
                  {GROUP_TIMELINE_SORT_LABELS[option]}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {onCollapseAll && (
        <button
          type="button"
          className="pm-hub-color-trigger"
          title="Collapse every project to one rollup bar"
          onClick={onCollapseAll}
        >
          <ChevronsUpDown size={13} strokeWidth={2} aria-hidden />
          <span>Collapse all</span>
        </button>
      )}
      {onExpandAll && (
        <button
          type="button"
          className="pm-hub-color-trigger"
          title="Expand every project to show its tasks"
          onClick={onExpandAll}
        >
          <ChevronsDownUp size={13} strokeWidth={2} aria-hidden />
          <span>Expand all</span>
        </button>
      )}
      {pinnedCount > 0 && onClearPins && (
        <button
          type="button"
          className="pm-hub-color-trigger"
          title={pinnedCount >= pinLimit ? `Compare strip is full (${pinLimit})` : 'Clear pinned compare rows'}
          onClick={onClearPins}
        >
          <span>
            Pinned {pinnedCount}{pinnedCount >= pinLimit ? ` (max)` : ''}
            {' · '}
            Clear
          </span>
        </button>
      )}
    </div>
  );
};

export default GroupTimelineViewControls;
